import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import React from "react";

// Pages - Operational Portal Only
import DirectoryPage from "@/pages/DirectoryPage";
import MemberProfilePage from "@/pages/MemberProfilePage";
import LoginPage from "@/pages/LoginPage";
import IntentFormPage from "@/pages/IntentFormPage";
import VerificationPage from "@/pages/VerificationPage";
import ApplicationFormPage from "@/pages/ApplicationFormPage";
import MemberDashboard from "@/pages/MemberDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CommitteePortal from "@/pages/CommitteePortal";
import AuthCallback from "@/pages/AuthCallback";
import PaymentCallback from "@/pages/PaymentCallback";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to automatically include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("session_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth context
export const AuthContext = React.createContext(null);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user: contextUser, setUser } = React.useContext(AuthContext);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [localUser, setLocalUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (contextUser) {
      setLocalUser(contextUser);
      setIsAuthenticated(true);
      return;
    }

    if (location.state?.user) {
      setLocalUser(location.state.user);
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const token = localStorage.getItem("session_token");
      if (!token) {
        setIsAuthenticated(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setLocalUser(response.data);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem("session_token");
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state, contextUser, setUser]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(localUser?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  
  // Handle Emergent OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Landing Page - shows role-based entry options */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/join" element={<IntentFormPage />} />
      <Route path="/verify" element={<VerificationPage />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Directory - accessible to logged in users */}
      <Route path="/directory" element={
        <ProtectedRoute>
          <DirectoryPage />
        </ProtectedRoute>
      } />
      <Route path="/directory/:userId" element={
        <ProtectedRoute>
          <MemberProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Applicant/Member Routes */}
      <Route path="/application" element={
        <ProtectedRoute>
          <ApplicationFormPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MemberDashboard />
        </ProtectedRoute>
      } />
      
      {/* Committee Routes */}
      <Route path="/committee" element={
        <ProtectedRoute allowedRoles={["committee", "admin"]}>
          <CommitteePortal />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Fallback - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("session_token");
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("session_token");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <div className="App min-h-screen flex flex-col bg-white">
        <BrowserRouter>
          <Navbar />
          <main className="flex-1">
            <AppRouter />
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
